import React, { useState, useCallback } from 'react';
import { Field, reduxForm, untouch, touch } from 'redux-form';
import { useDispatch } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import { trackAnalyticsEvent } from '../../../actions';
import { ANALYTICS_EVENTS } from '../../../lib/analytics';
import { MultilineText } from '../../fields';
import * as V from '../../../lib/validations';
import SimpleDocumentList from '../../fields/SimpleDocumentList';
import UploadButton from '../../fields/UploadButton';
import {
  ModalFooter,
  ModalHeader,
  DocumentSectionTitle,
  generateDateComponentsToString,
} from './DocumentVerificationComponents';
import { CandidateExplanationModalProps } from './utils/interfaces';

import './DocumentInfoModal.scss';

const MAX_CANDIDATE_EXPLANATION_LENGTH = 250;
const CANDIDATE_EXPLANATION = 'candidate_explanation';

// EVERI-3503: format a Filestack byte size for the uploaded-doc rows (e.g.
// "123 KB"), matching the apply flow (EVERI-3504).
const formatFileSize = (bytes?: number): string => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

// EVERI-3503: line-separated rows for the supporting docs uploaded via the
// modal (file icon + filename + size + remove), matching the apply layout.
const UploadedSupportingDocs: React.FC<{
  documents: any[];
  onRemoveByUploadId: (uploadId: string, exceptionId: string) => void;
}> = ({ documents, onRemoveByUploadId }) => {
  if (!documents?.length) return null;
  return (
    <ul className='candidate-explanation__uploaded-list'>
      {documents.map(doc => (
        <li
          key={`uploaded-${doc.uploadId || doc.handle}`}
          className='candidate-explanation__uploaded-item'
        >
          <M.Icon
            className='candidate-explanation__uploaded-icon'
            icon={
              doc.mimetype === 'application/pdf' ? 'DocumentPdf' : 'Document'
            }
            size={20}
          />
          <div className='candidate-explanation__uploaded-body'>
            <div className='candidate-explanation__uploaded-name'>
              {doc.filename}
            </div>
            {formatFileSize(doc.size) && (
              <div className='candidate-explanation__uploaded-size'>
                {formatFileSize(doc.size)}
              </div>
            )}
          </div>
          <button
            type='button'
            className='candidate-explanation__uploaded-remove'
            aria-label={i18n.getStr('buttons.remove')}
            data-testid='remove-supporting-doc'
            onClick={() => onRemoveByUploadId(doc.uploadId, doc.exceptionId)}
          >
            <M.Icon icon='Close' size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
};

const CandidateExplanationModal: React.FC<CandidateExplanationModalProps> = ({
  model,
  documentsRequired,
  policy,
  candidateExplanation,
  setCandidateExplanation,
  setIsModalOpen,
  handleSubmitExplanation,
  additionalDocuments,
  onRemoveByUploadId,
  handleModalUploadSuccess,
  isYearly,
  startDate,
  aiOcrEnabled,
  requestSubmit,
}) => {
  const dispatch = useDispatch();
  const [isReviewSection, setisReviewSection] = useState(false);
  const [remainingCharacterCount, setRemainingCharacterCount] = useState(
    MAX_CANDIDATE_EXPLANATION_LENGTH - candidateExplanation.length,
  );
  const handleCandidateExplanationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCandidateExplanation(e.target.value);
      setRemainingCharacterCount(
        MAX_CANDIDATE_EXPLANATION_LENGTH - e.target.value.length,
      );
    },
    [setCandidateExplanation],
  );

  const hasExplanation =
    remainingCharacterCount < MAX_CANDIDATE_EXPLANATION_LENGTH;

  // EVERI-3503: on-flag step 1 is now info-only (delay warning + missing-docs
  // list), so Continue always advances to step 2 — matching the apply flow.
  // Off-flag, step 1 remains the upload screen and Continue advances as before.
  const onContinue = () => {
    setisReviewSection(true);
  };

  const onSubmit = async () => {
    if (remainingCharacterCount < MAX_CANDIDATE_EXPLANATION_LENGTH) {
      setIsModalOpen(false);
      await handleSubmitExplanation();
      if (aiOcrEnabled) {
        // EVERI-3325: on-flag there is no page-level Submit button. Closing
        // the modal + committing the explanation makes the verification form
        // valid; the panel owns submission and fires it once that's true
        // (see EmploymentMultidocPanel's pendingSubmit effect). Triggering
        // submit here directly would race the form's re-validation.
        requestSubmit?.();
      } else {
        const submitButton = document.querySelector(
          '[data-testid="submitButton"]',
        ) as HTMLButtonElement;
        submitButton?.click();
      }
    } else {
      dispatch(touch('candidateExplanationForm', CANDIDATE_EXPLANATION));
    }
  };

  const onCancel = () => {
    setCandidateExplanation('');
    dispatch(untouch('candidateExplanationForm', CANDIDATE_EXPLANATION));
    dispatch(
      trackAnalyticsEvent(ANALYTICS_EVENTS.MULTIDOC_EXPLANATION_CLOSED, {
        documentsRequired: documentsRequired.length,
        policy,
      }),
    );
    setIsModalOpen(false);
  };

  const reviewContent = generateDateComponentsToString(
    documentsRequired,
    policy,
    isYearly,
    startDate,
  );

  // Off-flag (aiOcrEnabled false): today's behavior, unchanged. Multi-doc
  // submission requires OCR, so this path only renders for non-OCR accounts
  // (and any 1327-only misconfig) — no regression.
  if (!aiOcrEnabled) {
    return (
      <div className='modal-explanation'>
        <div className='cp-modal-content'>
          <ModalHeader
            onCancel={onCancel}
            textContent={i18n.getStr(
              'components.employment.missingRequiredDocuments',
            )}
          />
          <div className='cp-modal-body'>
            <DocumentSectionTitle
              titleKey='components.Verification.EmploymentMultidocPanel.candidateExplanation'
              reviewKey={
                isReviewSection
                  ? 'components.Verification.EmploymentMultidocPanel.candidateExplanationReview'
                  : 'components.Verification.EmploymentMultidocPanel.uploadOtherDocuments'
              }
              reviewContent={reviewContent}
            />
            {isReviewSection ? (
              <Field
                name={CANDIDATE_EXPLANATION}
                component={MultilineText}
                maxLength={MAX_CANDIDATE_EXPLANATION_LENGTH}
                onChange={handleCandidateExplanationChange}
                validate={[V.required]}
                remainingCharacterCount={remainingCharacterCount}
              />
            ) : (
              <UploadButton
                name={model}
                kind='secondary'
                hideIcon
                buttonText='buttons.uploadDocument'
                buttonClass=''
                context='multi-documents'
                fileOptions={{ maxFiles: documentsRequired.length + 10 }}
                handleUploadSuccess={handleModalUploadSuccess}
              />
            )}
            {additionalDocuments.length > 0 && (
              <>
                <DocumentSectionTitle
                  titleKey='components.employment.otherDocuments'
                  headingLevel='h3'
                />
                <SimpleDocumentList
                  onRemoveByUploadId={onRemoveByUploadId}
                  documents={additionalDocuments}
                  useTagStyle
                />
              </>
            )}
          </div>
          <ModalFooter
            onCancel={onCancel}
            handleSubmit={isReviewSection ? onSubmit : onContinue}
            button={isReviewSection ? 'buttons.confirm' : 'buttons.continue'}
          />
        </div>
      </div>
    );
  }

  // On-flag (EVERI-3325): 2-step flow matching the locked design.
  //   Step 1: delay warning + missing-docs list + the (required) explanation.
  //   Step 2: same warning + list + an optional supporting-document upload.
  // Both steps reuse the shared ModalFooter (Cancel + Continue/Confirm); the
  // Continue primary stays greyed until the explanation is non-empty.
  const delayWarning = (
    <DocumentSectionTitle
      titleKey='components.employment.backgroundCheckCouldBeDelayed'
      reviewKey='components.employment.notProvidingCouldDelay'
      reviewContent={reviewContent}
    />
  );

  return (
    <div className='modal-explanation modal-explanation--ocr'>
      <div className='cp-modal-content'>
        <ModalHeader
          onCancel={onCancel}
          textContent={i18n.getStr(
            'components.employment.missingRequiredDocuments',
          )}
        />
        <div className='cp-modal-body'>
          {isReviewSection ? (
            // Step 2 (EVERI-3503): upload supporting docs + the required
            // explanation — matching apply's combined second step (fonts,
            // width, and spacing styled to match in DocumentInfoModal.scss).
            <div className='candidate-explanation-step2'>
              <div className='candidate-explanation__upload-section'>
                <h3 className='candidate-explanation__section-header'>
                  {i18n.getStr(
                    'components.employment.uploadSupportingDocuments',
                  )}
                </h3>
                <p className='candidate-explanation__section-helper'>
                  {i18n.getStr(
                    'components.employment.uploadSupportingDocsHelper',
                  )}
                </p>
                <UploadButton
                  name={model}
                  kind='secondary'
                  icon='Export'
                  buttonText='buttons.uploadDocuments'
                  buttonClass=''
                  context='multi-documents'
                  fileOptions={{ maxFiles: documentsRequired.length + 10 }}
                  handleUploadSuccess={handleModalUploadSuccess}
                />
                {additionalDocuments.length > 0 && (
                  <UploadedSupportingDocs
                    documents={additionalDocuments}
                    onRemoveByUploadId={onRemoveByUploadId}
                  />
                )}
              </div>
              <div className='candidate-explanation__explain-section'>
                <h3 className='candidate-explanation__section-header'>
                  {i18n.getStr('components.employment.explainWhyCantProvide')}
                </h3>
                <p className='candidate-explanation__section-helper'>
                  {i18n.getStr(
                    'components.employment.explainWhyCantProvideHelper',
                  )}
                </p>
                <Field
                  name={CANDIDATE_EXPLANATION}
                  component={MultilineText}
                  maxLength={MAX_CANDIDATE_EXPLANATION_LENGTH}
                  onChange={handleCandidateExplanationChange}
                  validate={[V.required]}
                  remainingCharacterCount={remainingCharacterCount}
                />
              </div>
            </div>
          ) : (
            // Step 1 (EVERI-3503): info-only — delay warning + missing-docs
            // list. No textarea; Continue always advances.
            <div className='candidate-explanation-step1'>{delayWarning}</div>
          )}
        </div>
        <ModalFooter
          onCancel={onCancel}
          cancelButton={
            isReviewSection
              ? 'buttons.cancel'
              : 'components.employment.backToUpload'
          }
          handleSubmit={isReviewSection ? onSubmit : onContinue}
          button={isReviewSection ? 'buttons.confirm' : 'buttons.continue'}
          submitDisabled={isReviewSection && !hasExplanation}
        />
      </div>
    </div>
  );
};

export default reduxForm<{}, CandidateExplanationModalProps>({
  form: 'candidateExplanationForm',
})(CandidateExplanationModal);
